/**
 * Cronos EVM addresses that act as counterparty for Crypto.org ↔ Cronos EVM native CRO
 * migration / bridge legs (wallet → bridge contract on send; bridge → wallet on receive).
 *
 * **Defaults:** `DEFAULT_CRONOS_BRIDGE_MIGRATION_CONTRACTS` includes known migration/bridge
 * contracts unless disabled (see below). Additional addresses: `CRONOS_BRIDGE_MIGRATION_CONTRACTS`.
 *
 * **Opt out of built-in defaults** (only env + optional `extra` args): set
 * `CRONOS_BRIDGE_DISABLE_DEFAULT_ALLOWLIST=1` or `true`.
 *
 * **Remove specific defaults** while keeping others: `CRONOS_BRIDGE_EXCLUDE_CONTRACTS` — comma or
 * whitespace separated `0x` addresses subtracted from the default list before merging env.
 */

/** Native CRO migration / bridge contract — e.g. large sends like tx `0x55b873e0…` to this `To`. */
export const DEFAULT_CRONOS_BRIDGE_MIGRATION_CONTRACTS: readonly string[] = [
  '0x8995909dc0960fc9c75b6031d683124a4016825b',
];

function envTruthy(name: string): boolean {
  const v =
    typeof process !== 'undefined' && process.env?.[name]
      ? String(process.env[name]).trim().toLowerCase()
      : '';
  return v === '1' || v === 'true' || v === 'yes';
}

/** When true, `mergedCronosBridgeContracts` does not add `DEFAULT_CRONOS_BRIDGE_MIGRATION_CONTRACTS`. */
export function cronosBridgeDefaultAllowlistDisabled(): boolean {
  return envTruthy('CRONOS_BRIDGE_DISABLE_DEFAULT_ALLOWLIST');
}

/** Addresses subtracted from the default list (after normalization). */
export function cronosBridgeExcludeContractsFromEnv(): Set<string> {
  const raw =
    typeof process !== 'undefined' && process.env?.CRONOS_BRIDGE_EXCLUDE_CONTRACTS
      ? process.env.CRONOS_BRIDGE_EXCLUDE_CONTRACTS
      : '';
  const s = new Set<string>();
  for (const part of raw.split(/[\s,]+/)) {
    const n = normalizeBridgeAddr(part);
    if (n) s.add(n);
  }
  return s;
}

export function normalizeBridgeAddr(a: string): string | null {
  const t = a.trim().toLowerCase();
  return t.startsWith('0x') && t.length === 42 ? t : null;
}

/** Addresses from `process.env.CRONOS_BRIDGE_MIGRATION_CONTRACTS` (Node / scripts). */
export function cronosBridgeContractsFromEnv(): Set<string> {
  const raw =
    typeof process !== 'undefined' && process.env?.CRONOS_BRIDGE_MIGRATION_CONTRACTS
      ? process.env.CRONOS_BRIDGE_MIGRATION_CONTRACTS
      : '';
  const s = new Set<string>();
  for (const part of raw.split(/[\s,]+/)) {
    const n = normalizeBridgeAddr(part);
    if (n) s.add(n);
  }
  return s;
}

/**
 * Merge built-in defaults (unless disabled), minus excludes, plus env, plus optional `extra`.
 */
export function mergedCronosBridgeContracts(extra?: readonly string[]): Set<string> {
  const out = new Set<string>();

  if (!cronosBridgeDefaultAllowlistDisabled()) {
    for (let i = 0; i < DEFAULT_CRONOS_BRIDGE_MIGRATION_CONTRACTS.length; i++) {
      const n = normalizeBridgeAddr(DEFAULT_CRONOS_BRIDGE_MIGRATION_CONTRACTS[i]!);
      if (n) out.add(n);
    }
    cronosBridgeExcludeContractsFromEnv().forEach((x) => {
      out.delete(x);
    });
  }

  cronosBridgeContractsFromEnv().forEach((x) => {
    out.add(x);
  });
  if (extra) {
    for (let i = 0; i < extra.length; i++) {
      const n = normalizeBridgeAddr(extra[i]!);
      if (n) out.add(n);
    }
  }
  return out;
}
